import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useTranslations } from 'gt-react'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

const ease = [0.22, 1, 0.36, 1] as const

/**
 * Pricing preview — three clean tiers, middle one elevated.
 * Not a full pricing page — just enough to communicate value
 * and drive clicks to /pricing or /sign-up.
 */
export function PricingPreview() {
  const t = useTranslations('pricing_preview')

  const tiers = [
    {
      key: 'personal',
      featured: false,
      hasPriceUnit: false,
    },
    {
      key: 'business',
      featured: true,
      hasPriceUnit: true,
    },
    {
      key: 'enterprise',
      featured: false,
      hasPriceUnit: false,
    },
  ]

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Subtle background gradient */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-muted/30 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease }}
          className="text-center"
        >
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t('heading') || 'Simple, transparent pricing'}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-base text-muted-foreground">
            {t('subheading') || 'Start free, scale as you grow. No hidden fees.'}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 lg:grid-cols-3 lg:items-center">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.key}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: 0.08 * index, ease }}
            >
              <div
                className={cn(
                  'relative rounded-2xl p-8 transition-all',
                  tier.featured
                    ? 'border-2 border-primary bg-card shadow-xl shadow-primary/5 lg:scale-105 lg:py-10'
                    : 'border border-border bg-card',
                )}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-block rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                      {t(`${tier.key}.badge`) || 'Most popular'}
                    </span>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                    {t(`${tier.key}.name`)}
                  </h3>
                  <div className="mt-3 flex items-baseline gap-1">
                    <span className="text-4xl font-bold tracking-tight">
                      {t(`${tier.key}.price`)}
                    </span>
                    {tier.hasPriceUnit ? (
                      <span className="text-sm text-muted-foreground">
                        {t(`${tier.key}.price_unit`)}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    {t(`${tier.key}.description`)}
                  </p>
                </div>

                <ul className="mt-6 space-y-2.5">
                  {[1, 2, 3].map((i) => {
                    const feature = t(`${tier.key}.feature_${i}`)
                    if (!feature) return null
                    return (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    )
                  })}
                </ul>

                <div className="mt-8">
                  <Link
                    to={tier.key === 'enterprise' ? '/sign-up' : '/sign-up'}
                    className={cn(
                      'flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-medium transition-all',
                      tier.featured
                        ? 'bg-primary text-primary-foreground hover:opacity-90 shadow-sm'
                        : 'border border-border bg-transparent hover:bg-muted',
                    )}
                  >
                    {t(`${tier.key}.cta`)}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
